///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { GtthHistoryService } from '../logical-topology/engine/history-service';
import { ActionsToolbarModeEnum, UserDTO } from '../../generated/shared-types.model';
import { GtthLockProjectCommand } from '../logical-topology/engine/commands/project/lock-project-command';
import { UserAuthorities } from '../../services/user/user-authorities';
import { GtthSelectAreaModeEnum, GtthSquareSelectionService } from '../logical-topology/engine/square-selection-service';
import { GtthProjectStateService } from '../logical-topology/engine/project-state.service';
import { Subscription } from 'rxjs';
import { Principal } from '../../shared/auth/principal.service';
import { GtthRequestLockFromAnotherUserCommand } from '../logical-topology/engine/commands/project/request-lock-from-another-user-command';

@Component({
    selector: 'winde-global-actions-toolbar',
    templateUrl: './global-actions-toolbar.component.html',
    styleUrls: ['./global-actions-toolbar.scss'],
    standalone: false
})
export class GtthGlobalActionToolbarComponent implements OnInit, OnDestroy {
    modeType = ActionsToolbarModeEnum;
    public selectAreaModeEnum = GtthSelectAreaModeEnum;
    public isProjectLocked: boolean;
    public lockButtonTooltipText = '';
    public potentiallyCorruptedProject = false;
    @Output() stateChanged = new EventEmitter<any>();
    public readonly squareSelectionService: GtthSquareSelectionService;
    protected readonly UserAuthorities = UserAuthorities;
    private lockChanges: Subscription;
    private isUserInSameDomain: boolean;

    constructor(
        public readonly historyService: GtthHistoryService,
        public readonly projectState: GtthProjectStateService,
        private readonly principal: Principal
    ) {
        this.squareSelectionService = this.historyService.selectAreaService;

        this.principal.identity().then((_user: UserDTO) => {
            this.lockChanges = this.projectState.subscribeToLockChanges().subscribe((lockedBy: string) => {
                if (lockedBy) {
                    this.isProjectLocked = true;
                    this.isUserInSameDomain = principal.currentUserInTheSameDomain(lockedBy);
                    if (this.isUserInSameDomain) {
                        this.lockButtonTooltipText =
                            lockedBy + ' is currently editing the project. You can request a lock by clicking on this button.';
                    } else {
                        this.lockButtonTooltipText =
                            "Somebody else is currently editing the project. You can clone it using 'Save as' option.";
                    }
                } else {
                    this.isProjectLocked = false;
                    this.isUserInSameDomain = false;

                    this.lockButtonTooltipText = 'Project is not locked!';
                }
            });
        });
    }

    ngOnInit(): void {
        this.potentiallyCorruptedProject = this.projectState.isPotentiallyCorruptedProject();
    }

    ngOnDestroy(): void {
        if (this.lockChanges) {
            this.lockChanges.unsubscribe();
            this.lockChanges = undefined;
        }
    }

    selectAreaModeChanged(selectAreaType: GtthSelectAreaModeEnum) {
        this.historyService.selectAreaService.selectAreaMode = selectAreaType;
    }

    async projectLockOrRequestLock(): Promise<void> {
        if (!this.isProjectLocked) {
            const lockProjectCommand = this.historyService.command(GtthLockProjectCommand).create(true);
            await this.historyService.doCommandBypassHistory(lockProjectCommand);
        } else if (this.isUserInSameDomain) {
            const requestLockCommand = this.historyService
                .command(GtthRequestLockFromAnotherUserCommand)
                .create(this.projectState.getProjectLockedBy());
            let approved = await this.historyService.doCommandBypassHistory(requestLockCommand);

            if (approved) {
                const lockProjectCommand = this.historyService.command(GtthLockProjectCommand).create(true);
                approved = await this.historyService.doCommandBypassHistory(lockProjectCommand);
            }

            if (approved) {
                this.historyService.notificationService.showSuccess('Project is successfully locked');
            } else {
                this.historyService.notificationService.showWarning(
                    this.projectState.getProjectLockedBy() +
                        " rejected your lock request. You can clone this project using 'Save as' option and proceed working with the copy."
                );
            }
        }
    }
}
